import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { PlanTier } from "@/lib/planGating";

export interface TrialState {
  trialDecisionsUsed: number;
  trialActivatedAt: string | null;
  trialRemaining: number;
  trialExhausted: boolean;
  isTrialActive: boolean;
}

/**
 * DB-backed plan hook with trial state.
 * For signed-in users, reads current_plan + trial columns from profiles.
 * For guests, always returns "guest" with no trial.
 * Legacy "starter" users are mapped to "pro" access.
 */
export function usePlan(): { plan: PlanTier; loading: boolean; trial: TrialState } {
  const { user } = useAuth();
  const [plan, setPlan] = useState<PlanTier>(user ? "free" : "guest");
  const [loading, setLoading] = useState(!!user);
  const [trial, setTrial] = useState<TrialState>({
    trialDecisionsUsed: 0,
    trialActivatedAt: null,
    trialRemaining: 5,
    trialExhausted: false,
    isTrialActive: false,
  });

  useEffect(() => {
    if (!user) {
      setPlan("guest");
      setLoading(false);
      setTrial({ trialDecisionsUsed: 0, trialActivatedAt: null, trialRemaining: 5, trialExhausted: false, isTrialActive: false });
      return;
    }

    let cancelled = false;
    setLoading(true);

    supabase
      .from("profiles")
      .select("current_plan, trial_decisions_used, trial_activated_at")
      .eq("user_id", user.id)
      .single()
      .then(({ data }) => {
        if (!cancelled) {
          let p = (data?.current_plan as PlanTier) || "free";
          // Legacy migration: map "starter" to "pro" access
          if ((p as string) === "starter") p = "pro";
          setPlan(p);

          const used = (data as any)?.trial_decisions_used ?? 0;
          const activatedAt = (data as any)?.trial_activated_at ?? null;
          const remaining = Math.max(0, 5 - used);
          const exhausted = activatedAt !== null && used >= 5;
          const active = p === "free" && activatedAt !== null && !exhausted;

          setTrial({
            trialDecisionsUsed: used,
            trialActivatedAt: activatedAt,
            trialRemaining: remaining,
            trialExhausted: exhausted,
            isTrialActive: active,
          });
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [user]);

  return { plan, loading, trial };
}

/**
 * Check if a feature is available for a given plan tier.
 * Takes plan as input — never reads from localStorage.
 */
export function canUseFeatureForPlan(plan: PlanTier, feature: "save" | "track" | "alert" | "audit" | "full_routes" | "auto_buy"): boolean {
  if (plan === "elite") return true;
  if (plan === "pro") return feature !== "auto_buy";
  // Free users get 1 free save (handled in component logic)
  return false;
}
